import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { HistoryEntry } from '../types/history';

const MAX_STORAGE_BYTES = 4 * 1024 * 1024; // 4MB guard

interface HistoryState {
  entries: HistoryEntry[];
  selectedId: string | null;

  addEntry: (entry: Omit<HistoryEntry, 'id' | 'timestamp'>) => void;
  selectEntry: (id: string | null) => void;
  deleteEntry: (id: string) => void;
  clearAll: () => void;
}

export const useHistoryStore = create<HistoryState>()(
  persist(
    (set, get) => ({
      entries: [],
      selectedId: null,

      addEntry: (entry) => {
        const newEntry: HistoryEntry = {
          ...entry,
          id: crypto.randomUUID(),
          timestamp: Date.now(),
        };

        let entries = [newEntry, ...get().entries];

        // Trim oldest entries if storage exceeds limit
        while (
          entries.length > 1 &&
          JSON.stringify(entries).length > MAX_STORAGE_BYTES
        ) {
          entries = entries.slice(0, -1);
        }

        set({ entries, selectedId: newEntry.id });
      },

      selectEntry: (id) => set({ selectedId: id }),

      deleteEntry: (id) =>
        set((s) => ({
          entries: s.entries.filter((e) => e.id !== id),
          selectedId: s.selectedId === id ? null : s.selectedId,
        })),

      clearAll: () => set({ entries: [], selectedId: null }),
    }),
    {
      name: 'grace-history',
    }
  )
);
